import * as DateUtil from '@/lib/dateutils';
import type {VersionInfo} from '@/rest/api';

/**
 * A build timestamp in the viewer's local time, the same format for the client and the server
 * (OWA-1304). The server sends an ISO 8601 instant in UTC; the client bakes one in at build time.
 *
 * @param {string | undefined} iso - An ISO 8601 instant
 * @returns {string | null} The local date and time, or null when there is none or it is invalid
 */
export function formatBuildTime(iso: string | undefined): string | null {
    if (!iso) return null;
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;
    return DateUtil.formatDateTime(date);
}

/**
 * The server's commit as shown in brackets, with `-dirty` when it was built from uncommitted
 * changes. The server reports that only in its `git describe` output (serverTag).
 *
 * @param {VersionInfo} info
 * @returns {string | undefined}
 */
export function serverBuildCommit(info: VersionInfo): string | undefined {
    if (!info.serverCommit) return undefined;
    return info.serverTag?.endsWith('-dirty') ? `${info.serverCommit}-dirty` : info.serverCommit;
}

/**
 * The server's release tag, only when the build is exactly a release (`server/v6.7.4`). A
 * `git describe` result that is the commit itself, or a distance from a tag
 * (`server/v6.7.4-3-gabc1234`), adds nothing to the commit and is left out.
 *
 * @param {VersionInfo} info
 * @returns {string | undefined}
 */
export function serverReleaseTag(info: VersionInfo): string | undefined {
    const tag = info.serverTag?.replace(/-dirty$/, '');
    if (!tag) return undefined;
    if (info.serverCommit && (tag.startsWith(info.serverCommit) || info.serverCommit.startsWith(tag))) return undefined;
    if (/-\d+-g[0-9a-f]+$/.test(tag)) return undefined;
    return tag;
}
